import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
    MAX_RATING,
    MIN_RATING,
    formatRating,
    ratingAccessibilityText,
    starFills,
    stepRating,
} from '@aluvo/constants/src/ratings';
import styles from './StarRating.module.css';

// Keyboard steps are saved once the user pauses, so holding an arrow key
// doesn't send a write per step.
const KEYBOARD_COMMIT_MS = 700;

const STAR_PATH = 'M12 2.6l2.93 5.94 6.56.95-4.75 4.63 1.12 6.53L12 17.57l-5.86 3.08 1.12-6.53L2.51 9.49l6.56-.95L12 2.6z';

function StarIcon({ fill }) {
    return (
        <span className={styles.icon} data-fill={fill} aria-hidden="true">
            <svg viewBox="0 0 24 24" className={styles.outline}><path d={STAR_PATH} /></svg>
            {fill !== 'empty' && (
                <span className={fill === 'half' ? styles.fillHalf : styles.fillFull}>
                    <svg viewBox="0 0 24 24" className={styles.solid}><path d={STAR_PATH} /></svg>
                </span>
            )}
        </span>
    );
}

/**
 * Five-star, half-step rating control (0.5 … 5).
 *
 * Pointer: hovering previews; clicking the left half of a star picks .5, the
 * right half the whole star. Clicking the value already selected does nothing
 * (removal is a separate, confirmed action, so a stray click never clears).
 * Keyboard: the row is one role="slider" — ←/↓ and →/↑ step by 0.5, Home/End
 * jump to 0.5 / 5, Enter saves at once, Delete/Backspace asks to remove.
 *
 * The component never talks to the API: `onChange(value)` and `onRemove()`
 * belong to the page, which does the optimistic save and any auth gate.
 */
export default function StarRating({
    value = null,
    onChange,
    onRemove,
    onInteract,
    disabled = false,
    busy = false,
    label = 'Your rating',
    hint = null,
}) {
    const labelId = useId();
    const [preview, setPreview] = useState(null);
    const [draft, setDraft] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const timerRef = useRef(null);
    const inactive = disabled || busy;

    useEffect(() => () => clearTimeout(timerRef.current), []);
    useEffect(() => { if (value == null) setConfirming(false); }, [value]);

    const commit = useCallback((next) => {
        clearTimeout(timerRef.current);
        setDraft(null);
        if (inactive || next == null || next === value) return;
        onChange?.(next);
    }, [inactive, onChange, value]);

    const scheduleCommit = (next) => {
        setDraft(next);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => commit(next), KEYBOARD_COMMIT_MS);
    };

    const handleKeyDown = (event) => {
        if (inactive) return;
        const current = draft ?? value;
        let next;
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowUp':
                next = stepRating(current, 1);
                break;
            case 'ArrowLeft':
            case 'ArrowDown':
                next = current == null ? null : stepRating(current, -1);
                break;
            case 'Home':
                next = MIN_RATING;
                break;
            case 'End':
                next = MAX_RATING;
                break;
            case 'Enter':
                if (draft != null) {
                    event.preventDefault();
                    commit(draft);
                }
                return;
            case 'Delete':
            case 'Backspace':
                if (value != null && onRemove) {
                    event.preventDefault();
                    setConfirming(true);
                }
                return;
            default:
                return;
        }
        event.preventDefault();
        if (next == null) return;
        onInteract?.();
        scheduleCommit(next);
    };

    const handleBlur = () => {
        if (draft != null) commit(draft);
    };

    const hover = (next) => {
        if (inactive) return;
        onInteract?.();
        setPreview(next);
    };

    const click = (next) => {
        onInteract?.();
        setPreview(null);
        commit(next);
    };

    const shown = preview ?? draft ?? value;
    const announced = draft ?? value;

    return (
        <div className={styles.root}>
            <div className={styles.row}>
                <span id={labelId} className={styles.label}>
                    {label}{value != null ? `: ${formatRating(value)}` : ''}
                </span>
                <div
                    role="slider"
                    tabIndex={disabled ? -1 : 0}
                    aria-labelledby={labelId}
                    aria-valuemin={MIN_RATING}
                    aria-valuemax={MAX_RATING}
                    aria-valuenow={announced ?? undefined}
                    aria-valuetext={ratingAccessibilityText(announced)}
                    aria-disabled={inactive || undefined}
                    aria-busy={busy || undefined}
                    className={`${styles.stars} ${inactive ? styles.inactive : ''} ${preview != null ? styles.previewing : ''}`}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    onMouseLeave={() => setPreview(null)}
                >
                    {starFills(shown).map((fill, index) => (
                        <span key={index} className={styles.star}>
                            <StarIcon fill={fill} />
                            {[index + 0.5, index + 1].map((half, side) => (
                                <span
                                    key={half}
                                    className={side === 0 ? styles.hitLeft : styles.hitRight}
                                    data-value={half}
                                    aria-hidden="true"
                                    onMouseEnter={() => hover(half)}
                                    onClick={() => click(half)}
                                />
                            ))}
                        </span>
                    ))}
                </div>
                {busy && <span className={styles.status} role="status">Saving…</span>}
                {!busy && value != null && onRemove && !confirming && (
                    <button type="button" className={styles.linkBtn} onClick={() => setConfirming(true)} disabled={disabled}>
                        Remove
                    </button>
                )}
            </div>
            {confirming && (
                <div className={styles.confirm} role="group" aria-label="Remove your rating?">
                    <span>Remove your rating?</span>
                    <button
                        type="button"
                        className={styles.confirmBtn}
                        onClick={() => { setConfirming(false); onRemove?.(); }}
                        disabled={inactive}
                    >
                        Remove
                    </button>
                    <button type="button" className={styles.linkBtn} onClick={() => setConfirming(false)}>Keep</button>
                </div>
            )}
            {hint && !confirming && <p className={styles.hint}>{hint}</p>}
        </div>
    );
}
